import React, { useState } from 'react';
import { Button } from 'framework/ui/mui-builder';
import MenuItem from '@mui/material/MenuItem';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useFieldHelper } from 'framework/ui/field';
import { capitalize } from 'lodash';
import { ChangeZoneProps } from './types';
import { Box } from '@mui/material';
import { SxProps } from '@mui/system';
import { DropdownMenu } from 'framework/ui/menu';
import { IssuesModal } from '../modals';
import { useDashboardFieldsContext } from '../context/DashboardFieldsContext';

interface ChangeZoneWithContextProps extends Partial<ChangeZoneProps> {
  buttonSx?: SxProps;
  availableOnlyForZoneView?: boolean;
}

const ChangeZone = ({
  zoneOptions: propZoneOptions,
  zones: propZones,
  currentZoneSource = 'retentionRiskPriority',
  predefinedIssues: propPredefinedIssues,
  globalData,
  callbacks: propCallbacks,
  buttonSx,
  availableOnlyForZoneView = false,
}: ChangeZoneWithContextProps) => {
  const context = useDashboardFieldsContext();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [targetZone, setTargetZone] = useState<string | undefined>(undefined);
  const { getValue } = useFieldHelper({ source: currentZoneSource });

  const allZoneOptions = propZoneOptions || context?.zoneOptions || [];
  const zones = propZones || context?.zones || {};
  const callbacks = propCallbacks || context?.changeZoneCallbacks;
  const predefinedIssues = propPredefinedIssues || context?.predefinedIssues;

  if (!callbacks || !allZoneOptions.length || !Object.keys(zones).length) {
    return null;
  }

  const currentZone = getValue();
  const zoneOptions = allZoneOptions.filter(option => {
    const optionKey = option.key.replace('zone-', '');
    return optionKey !== currentZone;
  });

  const handleZoneSelect = (zone: string) => {
    setTargetZone(zone);
    setIsModalOpen(true);
  };

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
    if (isModalOpen) {
      setTargetZone(undefined);
    }
  };

  const handleSubmit = async (issues: any[], data: any) => {
    if (callbacks?.onSubmit) {
      await callbacks.onSubmit({
        ...data,
        targetZone,
      });
    }
    setIsModalOpen(false);
  };

  if (availableOnlyForZoneView) {
    return (
      <Box
        sx={{
          padding: '4px 16px',
          minWidth: '104px',
          fontSize: 12,
          fontWeight: 200,
          color: 'text.secondary',
          ...buttonSx,
        }}
      >
        {capitalize(String(zones[currentZone as keyof typeof zones] || currentZone).toLowerCase())}
      </Box>
    );
  }

  return (
    <Box>
      <DropdownMenu
        popupId="change-zone-menu"
        sxPaper={{ minWidth: '104px' }}
        disablePortal={false}
        iconButton={
          <Button
            size="small"
            variant="text"
            sx={{
              padding: '4px 16px',
              minWidth: '104px',
              justifyContent: 'space-between',
              fontSize: 12,
              fontWeight: 200,
              ...buttonSx,
            }}
          >
            {capitalize(String(zones[currentZone as keyof typeof zones] || currentZone).toLowerCase())}
            <KeyboardArrowDownIcon sx={{ fontSize: '18px' }} />
          </Button>
        }
      >
        {zoneOptions.map(option => (
          <MenuItem key={option.key} onClick={() => handleZoneSelect(option.key.replace('zone-', ''))}>
            {option.label}
          </MenuItem>
        ))}
      </DropdownMenu>

      {isModalOpen && (
        <IssuesModal
          isOpen
          data={predefinedIssues || []}
          onClose={toggleModal}
          globalData={globalData}
          callbacks={{
            onSubmit: handleSubmit,
            onClose: toggleModal,
          }}
        />
      )}
    </Box>
  );
};

export default ChangeZone;
